import { Trans, t } from "@lingui/macro";
import cx from "classnames";
import partition from "lodash/partition";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Address } from "viem";

import { USD_DECIMALS } from "config/factors";
import { PROMOTED_TOKENS_ORDER } from "config/promotedTokens";
import type { SortDirection } from "context/SorterContext/types";
import {
  selectAvailablePerpChartTokens,
  selectAvailableSwapChartTokens,
} from "context/SyntheticsStateContext/selectors/chartSelectors";
import { selectChainId, selectTokensData } from "context/SyntheticsStateContext/selectors/globalSelectors";
import { selectIndexTokenStatsMap } from "context/SyntheticsStateContext/selectors/statsSelectors";
import {
  TokenOption,
  selectTradeboxChooseSuitableMarket,
  selectTradeboxGetMaxLongShortLiquidityPool,
  selectTradeboxMarketInfo,
  selectTradeboxTradeFlags,
  selectTradeboxTradeType,
} from "context/SyntheticsStateContext/selectors/tradeboxSelectors";
import { useSelector } from "context/SyntheticsStateContext/utils";
import {
  SubCategoryTab,
  TopLevelTab,
  cryptoSubCategoryOptions,
  subCategoryTabLabels,
  tradfiSubCategoryOptions,
  useTokensFavorites,
} from "context/TokensFavoritesContext/TokensFavoritesContextProvider";
import { PreferredTradeTypePickStrategy } from "domain/synthetics/markets/chooseSuitableMarket";
import { useMarketsListingDates } from "domain/synthetics/markets/useMarketsListingDates";
import { getMarketBaseName, getMarketPoolName } from "domain/synthetics/markets/utils";
import { IndexTokensStats } from "domain/synthetics/stats/marketsInfoDataToIndexTokensStats";
import { PriceDelta, PriceDeltaMap, TokenData, TokensData, use24hPriceDeltaMap } from "domain/synthetics/tokens";
import { use24hVolumes } from "domain/synthetics/tokens/use24Volumes";
import { TradeType } from "domain/synthetics/trade";
import { MissedCoinsPlace } from "domain/synthetics/userFeedback";
import { useMissedCoinsSearch } from "domain/synthetics/userFeedback/useMissedCoinsSearch";
import { stripBlacklistedWords, type Token } from "domain/tokens";
import { getMidPrice } from "domain/tokens/utils";
import { useLocalizedMap } from "lib/i18n";
import { formatAmountHuman, formatUsdPrice } from "lib/numbers";
import { EMPTY_ARRAY } from "lib/objects";
import { searchBy } from "lib/searchBy";
import { useBreakpoints } from "lib/useBreakpoints";
import { convertTokenAddress, getTokenVisualMultiplier, isChartAvailableForToken } from "sdk/configs/tokens";

import Button from "components/Button/Button";
import { EmptyTableContent } from "components/EmptyTableContent/EmptyTableContent";
import FavoriteStar from "components/FavoriteStar/FavoriteStar";
import { FavoriteTabs } from "components/FavoriteTabs/FavoriteTabs";
import { RecentlyListedBadge } from "components/FavoriteTabs/RecentlyListedBadge";
import SearchInput from "components/SearchInput/SearchInput";
import { Sorter, useSorterHandlers } from "components/Sorter/Sorter";
import { ButtonRowScrollFadeContainer } from "components/TableScrollFade/TableScrollFade";
import Tabs from "components/Tabs/Tabs";
import type { Option as TabOption } from "components/Tabs/types";
import TokenIcon from "components/TokenIcon/TokenIcon";

import ChevronDownIcon from "img/ic_chevron_down.svg?react";
import LongIcon from "img/long.svg?react";
import SearchIconComponent from "img/search.svg?react";
import ShortIcon from "img/short.svg?react";

import {
  applySubCategoryFilter,
  applyTopLevelFilter,
  getRecentlyListedTokenAddresses,
  isMarketRecentlyListed,
} from "./marketFilters";
import { ModeTabs } from "./ModeTabs";
import { SelectorBase, useSelectorClose } from "../SelectorBase/SelectorBase";

type Props = {
  selectedToken: Token | undefined;
  oneRowLabels?: boolean;
};

const SWAP_EXCLUDED_TOP_LEVEL_TABS: TopLevelTab[] = ["tradfi", "recently-listed"];

export default function ChartTokenSelector(props: Props) {
  const { selectedToken, oneRowLabels } = props;

  const marketInfo = useSelector(selectTradeboxMarketInfo);
  const { isSwap } = useSelector(selectTradeboxTradeFlags);
  const { mode } = useTokensFavorites("chart-token-selector");
  const poolName = marketInfo && !isSwap ? getMarketPoolName(marketInfo) : null;

  const { isMobile } = useBreakpoints();
  const shouldUsePerpPanelWidth = !isSwap || mode === "perp";

  return (
    <SelectorBase
      popoverPlacement="bottom-start"
      handleClassName={cx({
        "mr-24": oneRowLabels === false,
        "py-0 md:h-40": isSwap,
      })}
      desktopPanelClassName={cx("max-w-[100vw] shadow-md", {
        "w-[520px]": !shouldUsePerpPanelWidth,
        "w-[880px]": shouldUsePerpPanelWidth,
      })}
      chevronClassName="hidden"
      label={
        <Button variant="secondary">
          {selectedToken ? (
            <span
              className={cx("inline-flex gap-12 whitespace-nowrap pl-0 text-[13px]", {
                "items-start": !oneRowLabels,
                "items-center": oneRowLabels,
              })}
            >
              {isSwap && oneRowLabels ? (
                <div className="rounded-4 bg-blue-300 bg-opacity-[20%] px-7 py-4 text-blue-300">
                  <Trans>Swap</Trans>
                </div>
              ) : null}

              <div className="flex items-center gap-8">
                <TokenIcon symbol={selectedToken.symbol} displaySize={isMobile ? 32 : 20} />
                <div className="flex gap-2 md:items-center md:gap-8">
                  <span
                    className={cx("flex justify-start leading-base", {
                      "flex-col items-baseline gap-2": !oneRowLabels,
                      "flex-row items-center": oneRowLabels,
                    })}
                  >
                    <span className="text-start text-[13px] font-medium text-typography-primary">
                      {!isSwap && <>{getTokenVisualMultiplier(selectedToken)}</>}
                      {selectedToken.symbol}
                      {t`/USD`}
                    </span>
                    {poolName && (
                      <span
                        className={cx("text-12 font-normal text-typography-secondary", {
                          "ml-4": oneRowLabels,
                        })}
                      >
                        <span>[{poolName}]</span>
                      </span>
                    )}

                    {isSwap && !oneRowLabels ? (
                      <div className="text-blue-300">
                        <Trans>Swap</Trans>
                      </div>
                    ) : null}
                  </span>

                  <ChevronDownIcon className="inline-block size-16" />
                </div>
              </div>
            </span>
          ) : (
            "..."
          )}
        </Button>
      }
      modalLabel={t`Market`}
      mobileModalContentPadding={false}
    >
      <MarketsList />
    </SelectorBase>
  );
}

type SortField =
  | "lastPrice"
  | "24hChange"
  | "24hVolume"
  | "longLiquidity"
  | "shortLiquidity"
  | "combinedAvailableLiquidity"
  | "combinedOpenInterest"
  | "unspecified";

function MarketsList() {
  const chainId = useSelector(selectChainId);
  const perpTokens = useSelector(selectAvailablePerpChartTokens);
  const swapTokens = useSelector(selectAvailableSwapChartTokens);
  const tradeType = useSelector(selectTradeboxTradeType);
  const chooseSuitableMarket = useSelector(selectTradeboxChooseSuitableMarket);
  const tokensData = useSelector(selectTokensData);

  const {
    topLevelTab: storedTopLevelTab,
    subCategoryTab: storedSubCategoryTab,
    mode,
    setMode,
    setSubCategoryTab,
    favoriteTokens,
    toggleFavoriteToken,
  } = useTokensFavorites("chart-token-selector");

  const localizedSubCategoryLabels = useLocalizedMap(subCategoryTabLabels);

  const { listingDateByIndexToken } = useMarketsListingDates(chainId);

  const recentlyListedAddressesSet = useMemo(
    () => new Set(getRecentlyListedTokenAddresses(listingDateByIndexToken, Date.now())),
    [listingDateByIndexToken]
  );

  const tradeFlags = useSelector(selectTradeboxTradeFlags);

  const initialModeSyncRef = useRef(false);
  useEffect(() => {
    if (initialModeSyncRef.current) return;
    initialModeSyncRef.current = true;
    setMode(tradeFlags.isSwap ? "swap" : "perp");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isSwap = mode === "swap";
  const shouldFallbackToAll = isSwap && SWAP_EXCLUDED_TOP_LEVEL_TABS.includes(storedTopLevelTab);
  const topLevelTab = shouldFallbackToAll ? "all" : storedTopLevelTab;
  const subCategoryTab = shouldFallbackToAll ? "all" : storedSubCategoryTab;
  const availableTokens = isSwap ? swapTokens : perpTokens;

  const { availableChartTokens: options, availableChartTokenAddresses } = useMemo(() => {
    const availableChartTokens = availableTokens?.filter((token) => isChartAvailableForToken(chainId, token.symbol));
    const availableChartTokenAddresses = availableChartTokens?.map((token) => token.address as Address);

    return {
      availableChartTokens,
      availableChartTokenAddresses,
    };
  }, [availableTokens, chainId]);

  const recentlyListedCount = useMemo(() => {
    if (!options || recentlyListedAddressesSet.size === 0) return 0;
    return options.filter((t) => recentlyListedAddressesSet.has(t.address.toLowerCase())).length;
  }, [options, recentlyListedAddressesSet]);

  const populatedCryptoSubCats = useMemo(() => {
    const set = new Set<SubCategoryTab>();
    if (!options) return set;
    for (const cat of ["ai", "layer1", "layer2", "defi", "meme"] as const) {
      if (options.some((o) => o.categories?.includes(cat))) set.add(cat);
    }
    return set;
  }, [options]);

  const populatedTradfiSubCats = useMemo(() => {
    const set = new Set<SubCategoryTab>();
    if (!options) return set;
    for (const cat of ["commodities", "stocks", "indices", "fx"] as const) {
      if (options.some((o) => o.categories?.includes(cat))) set.add(cat);
    }
    return set;
  }, [options]);

  const cryptoSubCatTabs = useMemo<TabOption<SubCategoryTab>[]>(
    () =>
      cryptoSubCategoryOptions
        .filter((opt) => opt === "all" || populatedCryptoSubCats.has(opt))
        .map((opt) => ({
          value: opt,
          label: opt === "all" ? <Trans>All</Trans> : localizedSubCategoryLabels[opt],
        })),
    [populatedCryptoSubCats, localizedSubCategoryLabels]
  );

  const tradfiSubCatTabs = useMemo<TabOption<SubCategoryTab>[]>(
    () =>
      tradfiSubCategoryOptions
        .filter((opt) => opt === "all" || populatedTradfiSubCats.has(opt))
        .map((opt) => ({
          value: opt,
          label: opt === "all" ? <Trans>All</Trans> : localizedSubCategoryLabels[opt],
        })),
    [populatedTradfiSubCats, localizedSubCategoryLabels]
  );

  const dayPriceDeltaMap = use24hPriceDeltaMap(chainId, availableChartTokenAddresses);
  const dayVolumesData = use24hVolumes();
  const dayVolumes = dayVolumesData?.byIndexToken;
  const indexTokenStatsMap = useSelector(selectIndexTokenStatsMap).indexMap;

  const { isMobile, isSmallMobile } = useBreakpoints();

  const close = useSelectorClose();

  const { orderBy, direction, getSorterProps } = useSorterHandlers<SortField>(
    `chart-token-selector-${isSwap ? "spot" : "perp"}`
  );

  const [searchKeyword, setSearchKeyword] = useState("");

  const sortedTokens = useFilterSortTokens({
    chainId,
    options,
    searchKeyword,
    topLevelTab,
    subCategoryTab,
    recentlyListedAddressesSet,
    favoriteTokens,
    direction,
    orderBy,
    tokensData,
    dayPriceDeltaMap,
    dayVolumes,
    indexTokenStatsMap,
    isSwap,
  });

  const sortedDetails = useMemo(() => {
    if (!sortedTokens) {
      return EMPTY_ARRAY;
    }

    return sortedTokens.map((token) => {
      const wrappedAddress = convertTokenAddress(chainId, token.address, "wrapped") as Address;
      return {
        token,
        tokenData: tokensData?.[wrappedAddress],
        dayPriceDelta: dayPriceDeltaMap?.[token.address],
        dayVolume: dayVolumes?.[wrappedAddress],
        openInterestLong: indexTokenStatsMap?.[wrappedAddress]?.totalOpenInterestLong,
        openInterestShort: indexTokenStatsMap?.[wrappedAddress]?.totalOpenInterestShort,
        maxLeverage: indexTokenStatsMap?.[wrappedAddress]?.maxUiAllowedLeverage,
      };
    });
  }, [sortedTokens, chainId, tokensData, dayPriceDeltaMap, dayVolumes, indexTokenStatsMap]);

  useMissedCoinsSearch({
    searchText: searchKeyword,
    isEmpty: !sortedTokens?.length && topLevelTab === "all",
    isLoaded: Boolean(options?.length),
    place: MissedCoinsPlace.marketDropdown,
    mode,
  });

  const handleMarketSelect = useCallback(
    (tokenAddress: string, preferredTradeType?: PreferredTradeTypePickStrategy | undefined) => {
      setSearchKeyword("");
      close();

      const effectiveTradeType: TradeType = isSwap
        ? TradeType.Swap
        : tradeType === TradeType.Swap
          ? TradeType.Long
          : tradeType;

      chooseSuitableMarket(tokenAddress, preferredTradeType, effectiveTradeType);
    },
    [chooseSuitableMarket, close, isSwap, tradeType]
  );

  const rowVerticalPadding = cx("px-12 py-10", {
    "group-last-of-type/row:pb-8": !isMobile,
  });
  const rowHorizontalPadding = cx("pr-8");
  const thClassName = cx(
    "sticky top-0 z-10 whitespace-nowrap bg-slate-900 text-left text-[11px] font-medium uppercase text-typography-secondary",
    "first-of-type:text-left",
    rowVerticalPadding,
    rowHorizontalPadding
  );

  const tdClassName = cx(
    "text-body-small",
    isMobile ? "align-top" : "align-middle",
    rowVerticalPadding,
    rowHorizontalPadding
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter" && sortedTokens && sortedTokens.length > 0) {
        const token = sortedTokens[0];
        handleMarketSelect(token.address);
      }
    },
    [sortedTokens, handleMarketSelect]
  );

  const placeholder = useMemo(() => {
    if (isSwap) {
      return t`Search token`;
    }

    return t`Search market`;
  }, [isSwap]);

  const availableLiquidityLabel = isMobile ? (isSmallMobile ? t`LIQ.` : t`AVAIL. LIQ.`) : t`AVAILABLE LIQUIDITY`;

  return (
    <>
      <div className="flex flex-col">
        <div className="mb-4 flex items-center gap-12 px-12 pt-12 max-md:flex-col max-md:gap-8">
          <ModeTabs mode={mode} setMode={setMode} />
          <SearchInput
            className="w-full *:!text-body-medium"
            value={searchKeyword}
            setValue={setSearchKeyword}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
          />
        </div>

        <ButtonRowScrollFadeContainer>
          <FavoriteTabs
            favoritesKey="chart-token-selector"
            recentlyListedCount={recentlyListedCount}
            className="px-16"
            excludedTabs={isSwap ? SWAP_EXCLUDED_TOP_LEVEL_TABS : undefined}
            selectedValue={topLevelTab}
          />
        </ButtonRowScrollFadeContainer>
        {topLevelTab === "crypto" && populatedCryptoSubCats.size > 0 && (
          <ButtonRowScrollFadeContainer>
            <Tabs
              options={cryptoSubCatTabs}
              selectedValue={subCategoryTab}
              onChange={setSubCategoryTab}
              type="block"
              className="bg-slate-800/50 px-16"
              tabsWrapperClassName="gap-16"
              regularOptionClassname="!px-0 !pb-9 !pt-11 text-13"
            />
          </ButtonRowScrollFadeContainer>
        )}
        {topLevelTab === "tradfi" && populatedTradfiSubCats.size > 0 && (
          <ButtonRowScrollFadeContainer>
            <Tabs
              options={tradfiSubCatTabs}
              selectedValue={subCategoryTab}
              onChange={setSubCategoryTab}
              type="block"
              className="bg-slate-800/50 px-16"
              tabsWrapperClassName="gap-16"
              regularOptionClassname="!px-0 !pb-9 !pt-11 text-13"
            />
          </ButtonRowScrollFadeContainer>
        )}
      </div>
      <div
        className={cx({
          "max-h-[444px] overflow-x-auto": !isMobile,
        })}
      >
        <table className="text-body-small w-full border-separate border-spacing-0">
          <thead>
            <tr>
              <th className={cx(thClassName, isMobile ? "min-w-[18ch]" : "min-w-[28ch]")} colSpan={2}>
                <Trans>MARKET</Trans>
              </th>
              {isSwap ? (
                <>
                  <th className={thClassName}>
                    <Sorter {...getSorterProps("lastPrice")}>
                      {isSmallMobile ? <Trans>PRICE</Trans> : <Trans>LAST PRICE</Trans>}
                    </Sorter>
                  </th>
                  {!isMobile && (
                    <th className={thClassName}>
                      <Sorter {...getSorterProps("24hChange")}>
                        <Trans>24H%</Trans>
                      </Sorter>
                    </th>
                  )}
                </>
              ) : (
                <>
                  <th className={thClassName}>
                    <Sorter {...getSorterProps("lastPrice")}>
                      {isSmallMobile ? <Trans>PRICE</Trans> : <Trans>LAST PRICE</Trans>}
                    </Sorter>
                  </th>
                  {!isMobile && (
                    <th className={thClassName}>
                      <Sorter {...getSorterProps("24hChange")}>
                        <Trans>24H%</Trans>
                      </Sorter>
                    </th>
                  )}
                  <th className={thClassName}>
                    <Sorter {...getSorterProps("24hVolume")}>
                      {isSmallMobile ? <Trans>VOL.</Trans> : <Trans>24H VOL.</Trans>}
                    </Sorter>
                  </th>
                  {!isMobile && (
                    <>
                      <th className={thClassName} colSpan={2}>
                        <Sorter {...getSorterProps("combinedOpenInterest")}>
                          <Trans>OPEN INTEREST</Trans>
                        </Sorter>
                      </th>
                      <th className={thClassName} colSpan={2}>
                        <Sorter {...getSorterProps("combinedAvailableLiquidity")}>{availableLiquidityLabel}</Sorter>
                      </th>
                    </>
                  )}
                </>
              )}
            </tr>
          </thead>

          <tbody>
            {sortedDetails?.map(
              ({ token, tokenData, dayPriceDelta, dayVolume, openInterestLong, openInterestShort, maxLeverage }) => (
                <MarketListItem
                  key={token.address}
                  token={token}
                  tokenData={tokenData}
                  dayPriceDelta={dayPriceDelta}
                  dayVolume={dayVolume}
                  openInterestLong={openInterestLong}
                  openInterestShort={openInterestShort}
                  maxLeverage={maxLeverage}
                  isSwap={isSwap}
                  isMobile={isMobile}
                  isFavorite={favoriteTokens?.includes(token.address)}
                  onFavorite={toggleFavoriteToken}
                  rowVerticalPadding={rowVerticalPadding}
                  rowHorizontalPadding={rowHorizontalPadding}
                  tdClassName={tdClassName}
                  onMarketSelect={handleMarketSelect}
                  listingDate={listingDateByIndexToken[token.address]}
                />
              )
            )}
          </tbody>
        </table>
        {options && options.length > 0 && !sortedTokens?.length && (
          <EmptyTableContent
            isLoading={false}
            isEmpty={true}
            emptyText={
              searchKeyword.trim() ? (
                <div className="flex flex-col items-center gap-12">
                  <span className="text-12">
                    <Trans>No markets matched.</Trans>
                  </span>
                  <Button type="button" variant="secondary" onClick={() => setMode(isSwap ? "perp" : "swap")}>
                    {isSwap ? <Trans>Search in perpetuals markets</Trans> : <Trans>Search in swap markets</Trans>}
                    <SearchIconComponent className="size-16" />
                  </Button>
                </div>
              ) : (
                <Trans>No markets matched.</Trans>
              )
            }
          />
        )}
      </div>
    </>
  );
}

function useFilterSortTokens({
  chainId,
  options,
  searchKeyword,
  topLevelTab,
  subCategoryTab,
  recentlyListedAddressesSet,
  favoriteTokens,
  direction,
  orderBy,
  tokensData,
  dayPriceDeltaMap,
  dayVolumes,
  indexTokenStatsMap,
  isSwap,
}: {
  chainId: number;
  options: Token[] | undefined;
  searchKeyword: string;
  topLevelTab: TopLevelTab;
  subCategoryTab: SubCategoryTab;
  recentlyListedAddressesSet: Set<string>;
  favoriteTokens: string[];
  direction: SortDirection;
  orderBy: SortField;
  tokensData: TokensData | undefined;
  dayPriceDeltaMap: PriceDeltaMap | undefined;
  dayVolumes: Record<Address, bigint> | undefined;
  indexTokenStatsMap: Partial<IndexTokensStats> | undefined;
  isSwap: boolean;
}) {
  const filteredTokens: Token[] | undefined = useMemo(() => {
    if (!options) return undefined;

    const textMatched = searchKeyword.trim()
      ? searchBy(
          options,
          [
            (item) => stripBlacklistedWords(item.name),
            (item) => (isSwap ? item.symbol : `${getTokenVisualMultiplier(item)}${item.symbol}`),
            (item) => (item.searchAliases ?? []).join(" "),
          ],
          searchKeyword
        )
      : options;

    const afterTopLevel = applyTopLevelFilter(textMatched ?? [], {
      topLevelTab,
      favoriteAddresses: favoriteTokens,
      recentlyListedAddresses: recentlyListedAddressesSet,
    });

    return applySubCategoryFilter(afterTopLevel, { topLevelTab, subCategoryTab });
  }, [options, searchKeyword, isSwap, topLevelTab, subCategoryTab, favoriteTokens, recentlyListedAddressesSet]);

  const getMaxLongShortLiquidityPool = useSelector(selectTradeboxGetMaxLongShortLiquidityPool);

  const sortedTokens = useMemo(() => {
    const [favorites, nonFavorites] = partition(filteredTokens, (token) => favoriteTokens.includes(token.address));

    const sorter = tokenSortingComparatorBuilder({
      chainId,
      orderBy,
      direction,
      tokensData,
      dayPriceDeltaMap,
      dayVolumes,
      indexTokenStatsMap,
      getMaxLongShortLiquidityPool,
    });

    const sortedFavorites = favorites.slice().sort(sorter);

    const sortedNonFavorites = nonFavorites.slice().sort(sorter);

    return [...sortedFavorites, ...sortedNonFavorites];
  }, [
    filteredTokens,
    chainId,
    orderBy,
    direction,
    tokensData,
    dayPriceDeltaMap,
    dayVolumes,
    indexTokenStatsMap,
    getMaxLongShortLiquidityPool,
    favoriteTokens,
  ]);

  return sortedTokens;
}

const MarketLabel = ({ token }: { token: Token }) => {
  return (
    <span className="text-typography-secondary">
      <span className="text-typography-primary">{getMarketBaseName({ indexToken: token, isSpotOnly: false })}</span>
      {t`/USD`}
    </span>
  );
};

function MarketListItem({
  token,
  tokenData,
  dayPriceDelta,
  dayVolume,
  openInterestLong,
  openInterestShort,
  maxLeverage,
  isSwap,
  isMobile,
  isFavorite,
  onFavorite,
  rowVerticalPadding,
  rowHorizontalPadding,
  tdClassName,
  onMarketSelect,
  listingDate,
}: {
  token: Token;
  tokenData: TokenData | undefined;
  dayPriceDelta: PriceDelta | undefined;
  dayVolume: bigint | undefined;
  openInterestLong: bigint | undefined;
  openInterestShort: bigint | undefined;
  maxLeverage: number | undefined;
  isSwap: boolean;
  isMobile: boolean;
  isFavorite?: boolean;
  onFavorite: (address: string) => void;
  rowVerticalPadding: string;
  rowHorizontalPadding: string;
  tdClassName: string;
  onMarketSelect: (address: string, preferredTradeType?: PreferredTradeTypePickStrategy | undefined) => void;
  listingDate?: number;
}) {
  const getMaxLongShortLiquidityPool = useSelector(selectTradeboxGetMaxLongShortLiquidityPool);

  const { maxLongLiquidityPool, maxShortLiquidityPool } = getMaxLongShortLiquidityPool(token);

  const handleFavoriteClick = useCallback(
    (e: React.MouseEvent<HTMLTableCellElement>) => {
      e.stopPropagation();
      onFavorite(token.address);
    },
    [onFavorite, token.address]
  );

  const handleSelectLargePosition = useCallback(
    (e: React.MouseEvent<HTMLTableCellElement | HTMLTableRowElement>) => {
      e.stopPropagation();
      onMarketSelect(token.address, "largestPosition");
    },
    [onMarketSelect, token.address]
  );

  const handleSelectLong = useCallback(
    (e: React.MouseEvent<HTMLTableCellElement>) => {
      e.stopPropagation();
      onMarketSelect(token.address, TradeType.Long);
    },
    [onMarketSelect, token.address]
  );

  const handleSelectShort = useCallback(
    (e: React.MouseEvent<HTMLTableCellElement>) => {
      e.stopPropagation();
      onMarketSelect(token.address, TradeType.Short);
    },
    [onMarketSelect, token.address]
  );

  const dayPriceDeltaComponent = useMemo(() => {
    return (
      <div
        className={cx("numbers", {
          positive: dayPriceDelta?.deltaPercentage && dayPriceDelta?.deltaPercentage > 0,
          negative: dayPriceDelta?.deltaPercentage && dayPriceDelta?.deltaPercentage < 0,
        })}
      >
        {dayPriceDelta?.deltaPercentageStr || "-"}
      </div>
    );
  }, [dayPriceDelta]);

  if (isSwap) {
    return (
      <tr key={token.symbol} className="group/row cursor-pointer hover:bg-fill-surfaceHover">
        <td
          className={cx("pl-14 pr-6 text-center text-typography-secondary", rowVerticalPadding)}
          onClick={handleFavoriteClick}
        >
          <FavoriteStar isFavorite={isFavorite} className="!size-12" />
        </td>
        <td
          className={cx("text-body-medium w-full", rowVerticalPadding, rowHorizontalPadding)}
          onClick={handleSelectLargePosition}
        >
          <span className="flex items-center gap-4">
            <TokenIcon className="ChartToken-list-icon -my-5 mr-6" symbol={token.symbol} displaySize={16} />
            <span>{token.name}</span>
            <span className="font-medium text-typography-secondary">{token.symbol}</span>
            {isMarketRecentlyListed(listingDate, Date.now()) && <RecentlyListedBadge />}
          </span>
        </td>
        <td className={tdClassName}>
          <div className="flex flex-col gap-4">
            <span className="numbers">
              {tokenData
                ? formatUsdPrice(getMidPrice(tokenData.prices), { visualMultiplier: tokenData.visualMultiplier })
                : "-"}
            </span>
            {isMobile && <span>{dayPriceDeltaComponent}</span>}
          </div>
        </td>
        {!isMobile && <td className={tdClassName}>{dayPriceDeltaComponent}</td>}
      </tr>
    );
  }

  return (
    <tr
      key={token.symbol}
      className="group/row cursor-pointer hover:bg-fill-surfaceHover"
      onClick={handleSelectLargePosition}
    >
      <td
        className={cx("w-0 px-12 text-center text-typography-secondary", rowVerticalPadding)}
        onClick={handleFavoriteClick}
      >
        <FavoriteStar isFavorite={isFavorite} className="!size-12" />
      </td>
      <td className={cx("pl-4 text-[13px]", rowVerticalPadding, isMobile ? "pr-2" : "pr-8")}>
        <div className={cx("flex", isMobile ? "items-start" : "items-center")}>
          <TokenIcon className="ChartToken-list-icon mr-6" symbol={token.symbol} displaySize={16} />
          <span className={cx("flex flex-wrap items-center gap-6")}>
            <span className="font-medium leading-1">
              <MarketLabel token={token} />
            </span>
            <span className="rounded-full bg-slate-700 px-6 py-[1.5px] text-12 font-medium leading-[1.25] text-typography-secondary numbers">
              {maxLeverage ? `${maxLeverage}x` : "-"}
            </span>
            {isMarketRecentlyListed(listingDate, Date.now()) && <RecentlyListedBadge />}
          </span>
        </div>
      </td>

      <td className={tdClassName}>
        <div className="flex flex-col gap-4">
          <span className="numbers">
            {tokenData
              ? formatUsdPrice(getMidPrice(tokenData.prices), { visualMultiplier: tokenData.visualMultiplier })
              : "-"}
          </span>
          {isMobile && <span>{dayPriceDeltaComponent}</span>}
        </div>
      </td>
      {!isMobile && <td className={tdClassName}>{dayPriceDeltaComponent}</td>}
      <td className={cx(tdClassName, "numbers")}>
        {dayVolume ? formatAmountHuman(dayVolume, USD_DECIMALS, true) : "-"}
      </td>
      {!isMobile && (
        <>
          <td className={cx(tdClassName, "pr-4 numbers")}>
            <span className="inline-flex items-center gap-6">
              <LongIcon width={12} className="relative top-1 mb-2 opacity-70" />
              {formatAmountHuman(openInterestLong ?? 0n, USD_DECIMALS, true)}
            </span>
          </td>
          <td className={cx(tdClassName, "pl-4 numbers")}>
            <span className="inline-flex items-center gap-6">
              <ShortIcon width={12} className="relative top-1 mb-2 opacity-70" />
              {formatAmountHuman(openInterestShort ?? 0n, USD_DECIMALS, true)}
            </span>
          </td>
        </>
      )}

      {!isMobile ? (
        <>
          <td className={cx(tdClassName, "group pr-4 numbers hover:bg-slate-800")} onClick={handleSelectLong}>
            <div className="inline-flex items-center justify-end gap-6">
              <LongIcon width={12} className="relative top-1 mb-2 opacity-70" />
              {formatAmountHuman(maxLongLiquidityPool?.maxLongLiquidity, USD_DECIMALS, true)}
            </div>
          </td>
          <td className={cx(tdClassName, "group pl-4 numbers hover:bg-slate-800")} onClick={handleSelectShort}>
            <div className="inline-flex items-center justify-end gap-6">
              <ShortIcon width={12} className="relative top-1 mb-2 opacity-70" />
              {formatAmountHuman(maxShortLiquidityPool?.maxShortLiquidity, USD_DECIMALS, true)}
            </div>
          </td>
        </>
      ) : null}
    </tr>
  );
}

function tokenSortingComparatorBuilder({
  chainId,
  orderBy,
  direction,
  tokensData,
  dayPriceDeltaMap,
  dayVolumes,
  indexTokenStatsMap,
  getMaxLongShortLiquidityPool,
}: {
  chainId: number;
  orderBy: SortField;
  direction: SortDirection;
  tokensData: TokensData | undefined;
  dayPriceDeltaMap: PriceDeltaMap | undefined;
  dayVolumes: Record<Address, bigint> | undefined;
  indexTokenStatsMap: Partial<IndexTokensStats> | undefined;
  getMaxLongShortLiquidityPool: (token: Token) => {
    maxLongLiquidityPool: TokenOption;
    maxShortLiquidityPool: TokenOption;
  };
}) {
  const directionMultiplier = direction === "asc" ? 1 : -1;

  return (a: Token, b: Token) => {
    const aAddress = convertTokenAddress(chainId, a.address, "wrapped");
    const bAddress = convertTokenAddress(chainId, b.address, "wrapped");

    if (orderBy === "unspecified" || direction === "unspecified") {
      const promoted = PROMOTED_TOKENS_ORDER[chainId];
      if (promoted) {
        const aIdx = promoted.indexOf(a.address);
        const bIdx = promoted.indexOf(b.address);
        if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
        if (aIdx !== -1) return -1;
        if (bIdx !== -1) return 1;
      }
      return 0;
    }

    if (orderBy === "24hVolume") {
      const aVolume = dayVolumes?.[aAddress] || 0n;
      const bVolume = dayVolumes?.[bAddress] || 0n;
      return aVolume > bVolume ? directionMultiplier : -directionMultiplier;
    }

    if (orderBy === "lastPrice") {
      const aVisualMultiplier = BigInt(a.visualMultiplier ?? 1);
      const bVisualMultiplier = BigInt(b.visualMultiplier ?? 1);

      let aMidPrice = tokensData?.[aAddress]?.prices ? getMidPrice(tokensData[aAddress].prices) : 0n;
      aMidPrice *= aVisualMultiplier;
      let bMidPrice = tokensData?.[bAddress]?.prices ? getMidPrice(tokensData[bAddress].prices) : 0n;
      bMidPrice *= bVisualMultiplier;

      return aMidPrice > bMidPrice ? directionMultiplier : -directionMultiplier;
    }

    if (orderBy === "24hChange") {
      // Price delta map uses native addresses
      const aChange = dayPriceDeltaMap?.[a.address]?.deltaPercentage || 0;
      const bChange = dayPriceDeltaMap?.[b.address]?.deltaPercentage || 0;
      return aChange > bChange ? directionMultiplier : -directionMultiplier;
    }

    if (orderBy === "combinedAvailableLiquidity") {
      const { maxLongLiquidityPool: aLongLiq, maxShortLiquidityPool: aShortLiq } = getMaxLongShortLiquidityPool(a);
      const { maxLongLiquidityPool: bLongLiq, maxShortLiquidityPool: bShortLiq } = getMaxLongShortLiquidityPool(b);

      const aTotalLiq = aLongLiq.maxLongLiquidity + aShortLiq.maxShortLiquidity;
      const bTotalLiq = bLongLiq.maxLongLiquidity + bShortLiq.maxShortLiquidity;
      return aTotalLiq > bTotalLiq ? directionMultiplier : -directionMultiplier;
    }

    if (orderBy === "combinedOpenInterest") {
      const aOI =
        (indexTokenStatsMap?.[aAddress]?.totalOpenInterestLong || 0n) +
        (indexTokenStatsMap?.[aAddress]?.totalOpenInterestShort || 0n);
      const bOI =
        (indexTokenStatsMap?.[bAddress]?.totalOpenInterestLong || 0n) +
        (indexTokenStatsMap?.[bAddress]?.totalOpenInterestShort || 0n);
      return aOI > bOI ? directionMultiplier : -directionMultiplier;
    }

    return 0;
  };
}
